import React, { useState, useEffect, useCallback } from 'react';
import {
    Sparkles, AlertTriangle, Check, X, ShieldAlert, TrendingUp,
} from 'lucide-react';
import api from '../services/api';
import ErroreLettura from '../components/comuni/ErroreLettura';
import { conUnTentativo, motivoDellaLettura } from '../utils/primaLettura';
import '../styles/AmministrazioneAi.css';

/**
 * AmministrazioneAi — governo dell'assistenza.
 *
 * Serve a due cose: accendere e spegnere i compiti senza passare dal
 * database, e vedere quanto si sta spendendo.
 *
 * I compiti che trattano dati dei pazienti sono separati e con un
 * avviso: accenderli non deve essere un gesto distratto.
 */

const euro = (n) => (n == null ? '—' : Number(n).toFixed(2).replace('.', ',') + ' €');
const euroFine = (n) => (n == null ? '—' : Number(n).toFixed(4).replace('.', ',') + ' €');

const AmministrazioneAi = () => {
    const [compiti, setCompiti] = useState([]);
    const [consumo, setConsumo] = useState(null);
    const [caricamento, setCaricamento] = useState(true);
    const [avviso, setAvviso] = useState(null);
    const [errore, setErrore] = useState(null);
    const [conferma, setConferma] = useState(null);
    /* L'errore della lettura sta per conto suo: la lettura riuscita lo
       toglie, quello di un cambio di stato resta (primaLettura.js). */
    const [erroreLettura, setErroreLettura] = useState(null);

    const carica = useCallback(async () => {
        try {
            setCaricamento(true);
            const [c, u] = await Promise.all([
                conUnTentativo(() => api.get('/ai/compiti')),
                conUnTentativo(() => api.get('/ai/consumo')),
            ]);
            setCompiti(c.data.data.compiti || []);
            setConsumo(u.data.data);
            setErroreLettura(null);
        } catch (e) {
            setErroreLettura(motivoDellaLettura(e, 'Impossibile caricare i dati'));
        } finally {
            setCaricamento(false);
        }
    }, []);

    useEffect(() => { carica(); }, [carica]);

    const cambia = async (codice, attivo) => {
        try {
            setErrore(null);
            const r = await api.put(`/ai/compiti/${codice}`, { attivo });
            setAvviso(r.data.message);
            setConferma(null);
            await carica();
        } catch (e) {
            setErrore(e?.response?.data?.message || 'Errore nel cambio di stato');
        }
    };

    /* I compiti clinici non si accendono con un clic distratto: si
       chiede conferma, ricordando cosa comporta. */
    const chiediConferma = (c) => {
        if (c.tratta_pazienti && !c.attivo) setConferma(c);
        else cambia(c.codice, !c.attivo);
    };

    const amministrativi = compiti.filter((c) => !c.tratta_pazienti);
    const clinici = compiti.filter((c) => c.tratta_pazienti);

    const riga = (c) => (
        <div key={c.codice} className={`aia-compito ${c.attivo ? 'acceso' : ''}`}>
            <div className="aia-compito-testo">
                <b>{c.nome}</b>
                <p>{c.descrizione}</p>
                <div className="aia-dettagli">
                    <span>{c.modello}</span>
                    <span>{c.ruoli.join(', ')}</span>
                    {c.addebita_medico && <span className="aia-addebito">costo al medico</span>}
                </div>
            </div>
            <button
                className={c.attivo ? 'aia-spegni' : 'aia-accendi'}
                onClick={() => chiediConferma(c)}
            >
                {c.attivo ? 'Attivo' : 'Spento'}
            </button>
        </div>
    );

    if (caricamento) {
        return <div className="aia-wrap"><div className="aia-stato">Caricamento…</div></div>;
    }

    return (
        <div className="aia-wrap">
            <div className="aia-testata">
                <h1><Sparkles size={21} /> Consumi dell&apos;assistente AI</h1>
            </div>

            <ErroreLettura testo={erroreLettura} onRiprova={carica} classe="aia-errore" Contenitore="div" />
            {errore && (
                <div className="aia-errore">
                    <AlertTriangle size={16} /> {errore}
                    <button onClick={() => setErrore(null)}><X size={14} /></button>
                </div>
            )}
            {avviso && (
                <div className="aia-avviso">
                    <Check size={16} /> {avviso}
                    <button onClick={() => setAvviso(null)}><X size={14} /></button>
                </div>
            )}

            {/* ---------- consumo ---------- */}
            {consumo && (
                <section className="aia-sezione">
                    <h2><TrendingUp size={17} /> Consumo</h2>

                    {consumo.compiti.length === 0 ? (
                        <div className="aia-vuoto">Nessun utilizzo registrato.</div>
                    ) : (
                        <div className="aia-due">
                            <div>
                                <h3>Per compito</h3>
                                <table className="aia-tabella rm-scorre">
                                    <thead>
                                        <tr><th>Compito</th><th className="num">Richieste</th><th className="num">Costo</th></tr>
                                    </thead>
                                    <tbody>
                                        {consumo.compiti.map((x) => (
                                            <tr key={x.compito}>
                                                <td>{x.compito.replace(/_/g, ' ')}</td>
                                                <td className="num">{x.chiamate}</td>
                                                <td className="num">{euroFine(x.costo)}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>

                            <div>
                                <h3>Da scalare ai medici</h3>
                                {consumo.medici.filter((m) => m.medico_id).length === 0 ? (
                                    <div className="aia-vuoto">
                                        Nessun consumo attribuito a un medico.
                                    </div>
                                ) : (
                                    <table className="aia-tabella rm-scorre">
                                        <thead>
                                            <tr><th>Medico</th><th>Mese</th><th className="num">Da scalare</th></tr>
                                        </thead>
                                        <tbody>
                                            {consumo.medici.filter((m) => m.medico_id).map((m, i) => (
                                                <tr key={i}>
                                                    <td>{m.cognome} {m.nome}</td>
                                                    <td>{new Date(m.mese).toLocaleDateString('it-IT',
                                                        { month: 'long', year: 'numeric' })}</td>
                                                    <td className="num">{euro(m.da_scalare)}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                )}
                            </div>
                        </div>
                    )}
                </section>
            )}

            {/* ---------- compiti amministrativi ---------- */}
            <section className="aia-sezione">
                <h2>Attività amministrative</h2>
                <p className="aia-nota">
                    Non trattano dati dei pazienti.
                </p>
                {amministrativi.map(riga)}
            </section>

            {/* ---------- compiti clinici ---------- */}
            <section className="aia-sezione">
                <h2><ShieldAlert size={17} /> Attività che trattano dati dei pazienti</h2>
                <p className="aia-nota aia-nota--attenzione">
                    L'attivazione richiede che gli adempimenti privacy siano
                    completi: informativa aggiornata e sottoscritta, nomina a
                    responsabile del trattamento, valutazione d'impatto.
                    I dati identificativi vengono comunque rimossi prima
                    dell'invio, e i pazienti che si sono opposti restano esclusi.
                </p>
                {clinici.map(riga)}
            </section>

            {/* ---------- conferma ---------- */}
            {conferma && (
                <div className="aia-modale-sfondo" onClick={() => setConferma(null)}>
                    <div className="aia-modale" onClick={(e) => e.stopPropagation()}>
                        <h3><ShieldAlert size={19} /> Attivare "{conferma.nome}"?</h3>
                        <p>
                            Questo compito tratta dati dei pazienti. Attivandolo
                            dichiari che gli adempimenti privacy sono completi.
                        </p>
                        <p className="aia-modale-nota">
                            L'attivazione resta registrata con il tuo nome e la data.
                        </p>
                        <div className="aia-modale-azioni">
                            <button onClick={() => setConferma(null)}>Annulla</button>
                            <button className="aia-conferma"
                                    onClick={() => cambia(conferma.codice, true)}>
                                Attiva
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AmministrazioneAi;
